import React, { useMemo } from "react";

import { Box, TouchableBox, useLayoutContext, useTheme } from "arkh";

import { StyleSheet } from "react-native";
import Animated from "react-native-reanimated";
import { useStyles } from "./useStyles";

const itemWidth = 150;
const itemHeight = 30;

export function SegmentedControlNew<T extends string>(props: {
  items: {
    children: React.ReactNode;
    value: T;
    width?: number;
  }[];
  selected: T;
  setSelected: (selected: T) => void;
}) {
  const selectedIndex = props.items.findIndex(
    (item) => item.value === props.selected
  );
  const selectedItem = props.items[selectedIndex];
  const width = useMemo(
    () =>
      props.items.reduce((acc, item, i) => {
        if (i < selectedIndex) {
          return acc + (item.width ?? itemWidth);
        }
        return acc;
      }, 0),
    [props.items, selectedIndex]
  );

  const layout = useLayoutContext();
  const theme = useTheme();

  const animatingStyles = useStyles(width, selectedItem?.width ?? itemWidth);
  return (
    <Box
      cornerRadius="medium"
      color="background"
      borderColor="border-primary"
      style={styles.container}
    >
      <Animated.View
        style={[
          styles.selectedIndicator,
          {
            height: itemHeight,
            borderRadius: layout.radius.medium,
            backgroundColor: theme.surface,
          },
          animatingStyles,
        ]}
      />
      {props.items.map((item) => (
        <SegmentedControlItem
          key={item.value}
          width={item.width ?? itemWidth}
          selected={item.value === props.selected}
          onPress={() => props.setSelected(item.value)}
        >
          {item.children}
        </SegmentedControlItem>
      ))}
    </Box>
  );
}

function SegmentedControlItem(props: {
  selected: boolean;
  onPress: () => void;
  children: React.ReactNode;
  width: number;
}) {
  return (
    <TouchableBox
      style={{
        height: itemHeight,
        width: props.width,
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "row",
      }}
      onPress={() => {
        props.onPress();
      }}
    >
      {props.children}
    </TouchableBox>
  );
}

SegmentedControlNew.Item = SegmentedControlItem;
const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "center",
    borderWidth: 1,
  },
  selectedIndicator: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
  },
});
